import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { find, propEq } from 'ramda'
import React from 'react'

import { DynamicInputs, Input, InputCode, Select } from '@/app/component/atom/form'
import type { InputCodeRef } from '@/app/component/atom/form/input-code'
import PzMenu from '@/app/component/atom/pz-menu'
import Loading from '@/app/component/guard/loading'
import IconText from '@/app/component/layout/icon-text'
import CustomDialog from '@/app/component/wrapper/custom-dialog'
import { validateJSON } from '@/app/module/campaigns/api-helpers'
import validate, { hasPzURLError, hasRequiredError } from '@/app/service/validate'
import { PersonalizationType, SinglePersonalizationType } from '@/app/types'

import { LocalAPIAction } from '../../../types'

const API_ACTION_KEY = 'makeAPIRequest'
const API_ACTION_LABEL = 'Make API request (webhook)'
const API_ACTION_ICON = 'settings_remote'

type HeaderRow = {
  key: string
  value: string
}

type HeaderErrorRow = {
  key: boolean
  value: boolean
}

type ApiMethod = 'GET' | 'POST'

type Props = {
  deleteEnabled?: boolean
  isNewAction?: boolean
  loading?: boolean
  personalizationList?: PersonalizationType
  value: LocalAPIAction
  webhook?: boolean

  onClose: () => void
  onDelete?: () => void
  onSave: (value: Pick<LocalAPIAction, 'makeAPIRequest'>) => void
}

type State = {
  method: ApiMethod
  url: string
  payload: string
  error: {
    url: string
    payload: boolean
    headers: HeaderErrorRow[]
  }
  focus: {
    url: boolean
    payload: boolean
    headers: boolean
  }
  URLPzMenuAnchor: HTMLElement | null
  headers: HeaderRow[]
}

class API extends React.Component<Props, State> {
  bodyInput = React.createRef<InputCodeRef>()

  URLInput = React.createRef<HTMLInputElement>()

  getInitialState = (): State => {
    const apiValue = this.props.value?.makeAPIRequest
    const headers = apiValue?.headers ?? []

    return {
      method: (apiValue?.method as ApiMethod | undefined) ?? 'GET',
      url: apiValue?.url ?? '',
      payload: apiValue?.payload ?? '',
      error: {
        url: '',
        payload: apiValue?.method === 'POST' && !!apiValue.payload && !validateJSON(apiValue.payload),
        headers: headers.map(() => ({
          key: false,
          value: false,
        })),
      },
      focus: {
        url: false,
        payload: false,
        headers: false,
      },
      URLPzMenuAnchor: null,
      headers,
    }
  }

  state: State = this.getInitialState()

  componentDidMount() {
    this.checkHeader()
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (prevState.method !== this.state.method || prevState.headers.length !== this.state.headers.length) {
      this.checkHeader()
    }
  }

  apiMethods: Array<{ label: string; value: ApiMethod; disabled?: boolean }> = [
    {
      label: 'GET',
      value: 'GET',
      disabled: false,
    },
    {
      label: 'POST',
      value: 'POST',
    },
  ]

  toggleURLPzMenu = ({ currentTarget = null }: { currentTarget?: HTMLElement | null } = {}) => {
    this.setState({
      URLPzMenuAnchor: currentTarget,
    })
  }

  checkHeader = () => {
    if (this.state.method === 'POST' && this.state.headers.length < 1) {
      const { headers, error } = this.state
      headers.push({ key: 'Content-Type', value: 'application/json' })
      error.headers.push({
        key: false,
        value: false,
      })
      this.setState({
        headers,
        error,
      })
    }
  }

  addRow = () => {
    const { headers, error } = this.state
    headers.push({
      key: '',
      value: '',
    })
    error.headers.push({
      key: true,
      value: true,
    })
    this.setState({
      headers,
      error,
    })
  }

  render() {
    const { onSave, onClose, onDelete = () => {}, loading = false, personalizationList = {} } = this.props

    const isHeadersValid =
      !!find(propEq('key', true), this.state.error.headers) || !!find(propEq('value', true), this.state.error.headers)
    return (
      <Loading isLoading={loading}>
        <CustomDialog open={true} onClose={onClose}>
          <DialogTitle style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <IconText>
                <Icon>{API_ACTION_ICON}</Icon>
                <Typography variant="h6">{API_ACTION_LABEL}</Typography>
              </IconText>
              <IconButton onClick={onClose}>
                <Icon>close</Icon>
              </IconButton>
            </div>
          </DialogTitle>
          <DialogContent>
            <div
              style={{
                width: '70vw',
                minWidth: '300px',
                maxWidth: '100%',
              }}
            >
              <Select
                label="Method"
                name="api-request-action-select-method"
                value={this.state.method}
                values={this.apiMethods}
                onChange={({ value }: { value: ApiMethod }) => {
                  this.setState({
                    method: value,
                  })
                }}
              />
              <Input
                label="API URL"
                id="api-request-action-url"
                name="api-request-action-url"
                inputRef={this.URLInput}
                placeholder="https://example.com/"
                value={this.state.url}
                error={this.state.error.url}
                onChange={({ value }: { value: string }) => {
                  this.setState({
                    url: value,
                    error: {
                      ...this.state.error,
                      url: '',
                    },
                  })
                }}
                onBlur={({ value }) => {
                  const error = validate(hasRequiredError, hasPzURLError)(value)
                  if (error) {
                    this.setState({
                      error: {
                        ...this.state.error,
                        url: error || '',
                      },
                    })
                  }
                }}
              />
              {this.props.webhook && (
                <Typography component="div" variant="caption" style={{ color: 'rgba(0, 0, 0, 0.54)' }}>
                  Note: <StyledChip color="primary" label="to" />, <StyledChip label="from" color="primary" />, and{' '}
                  <StyledChip label="message" color="primary" /> parameters will be added to your webhook URL, e.g.,
                  https://www.example.com/?to=12345&from=12125551234&message=hello
                </Typography>
              )}
              <div style={{ display: 'flex', marginTop: '5px' }}>
                <PzMenu
                  id="api-request-action-url"
                  color="primary"
                  containerSx={{
                    marginRight: '0.5rem',
                  }}
                  personalizationList={personalizationList}
                  variant="outlined"
                  onSelect={(item: SinglePersonalizationType) => {
                    const urlInput = this.URLInput.current
                    if (!urlInput) {
                      return
                    }
                    const URLSelectionStart = urlInput.selectionStart || 0
                    let { url } = this.state
                    url = [url.slice(0, URLSelectionStart), `{{${item.value}}}`, url.slice(URLSelectionStart)].join('')
                    this.setState({
                      URLPzMenuAnchor: null,
                      url,
                    })
                    setTimeout(() => {
                      urlInput.focus()
                      const cursorPosition = URLSelectionStart + item.value.length + 4
                      urlInput.setSelectionRange(cursorPosition, cursorPosition)
                    }, 500)
                  }}
                />
              </div>
            </div>
            {this.state.method === 'POST' && (
              <div
                id="api-request-action-post-body-cont"
                style={{
                  width: '70vw',
                  minWidth: '300px',
                  maxWidth: '100%',
                  marginTop: '10px',
                }}
              >
                <InputLabel
                  error={this.state.error.payload}
                  focused={this.state.focus.payload}
                  style={{
                    marginBottom: '6px',
                  }}
                >
                  Body
                </InputLabel>
                <InputCode
                  actions={
                    <PzMenu
                      id="api-request-action-body"
                      color="primary"
                      personalizationList={personalizationList}
                      variant="outlined"
                      onSelect={(item: SinglePersonalizationType) => {
                        this.bodyInput.current?.insertText(`{{${item.value}}}`)
                      }}
                    />
                  }
                  error={this.state.error.payload ? 'Invalid JSON body' : ''}
                  highlightLine={false}
                  inputLanguage="json"
                  label="Body"
                  personalizationList={personalizationList}
                  placeholder="Add JSON Body"
                  ref={this.bodyInput}
                  showFoldGutter={false}
                  value={this.state.payload}
                  onBlur={() => {
                    this.setState({
                      focus: {
                        ...this.state.focus,
                        payload: false,
                      },
                    })
                  }}
                  onChange={(value) => {
                    this.setState((previous) => ({ payload: value, error: { ...previous.error, payload: false } }))
                  }}
                  onFocus={() => {
                    this.setState({
                      focus: {
                        url: false,
                        payload: true,
                        headers: false,
                      },
                    })
                  }}
                  onValidate={(diagnostics) => {
                    this.setState({
                      error: {
                        ...this.state.error,
                        payload: diagnostics.length > 0,
                      },
                    })
                  }}
                />
              </div>
            )}
            <div
              style={{
                marginTop: '20px',
              }}
            >
              <InputLabel error={isHeadersValid} focused={this.state.focus.headers}>
                Headers
              </InputLabel>
              {this.state.headers.length === 0 && (
                <IconButton onClick={this.addRow}>
                  <Icon>add_circle</Icon>
                </IconButton>
              )}
              <DynamicInputs
                firstRowLock={this.state.method === 'POST'}
                name="api-request-action-headers"
                values={this.state.headers}
                fields={['key', 'value']}
                onFocus={() => {
                  this.setState({
                    focus: {
                      url: false,
                      payload: false,
                      headers: true,
                    },
                  })
                }}
                onBlur={() => {
                  this.setState({
                    focus: {
                      ...this.state.focus,
                    },
                  })
                }}
                onChange={({ position, field, value }) => {
                  const { headers, error } = this.state
                  headers[position][field as keyof HeaderRow] = value
                  error.headers[position][field as keyof HeaderRow] = value === ''
                  this.setState({
                    headers,
                    error,
                  })
                }}
                removeRow={({ position }: { position: number }) => {
                  const { headers, error } = this.state
                  headers.splice(position, 1)
                  error.headers.splice(position, 1)
                  this.setState({
                    headers,
                    error,
                  })
                }}
                errors={this.state.error.headers}
                addRow={this.addRow}
              />
            </div>
          </DialogContent>
          <DialogActions>
            {this.props.deleteEnabled && (
              <div
                style={{
                  width: '100%',
                }}
              >
                <StyledDeleteButton onClick={onDelete} disabled={loading} size="small">
                  Remove Webhook
                </StyledDeleteButton>
              </div>
            )}
            <Button
              variant="contained"
              color="primary"
              disabled={
                this.state.url.length === 0 ||
                !!this.state.error.payload ||
                !!this.state.error.url ||
                isHeadersValid ||
                loading
              }
              onClick={() =>
                onSave({
                  [API_ACTION_KEY]: {
                    method: this.state.method,
                    url: this.state.url,
                    payload: this.state.method === 'POST' ? this.state.payload : '',
                    headers: this.state.headers,
                  },
                })
              }
            >
              Save
            </Button>
          </DialogActions>
        </CustomDialog>
      </Loading>
    )
  }
}

const StyledChip = styled(Chip)(() => ({
  height: '16px',
  fontSize: '12px',
  lineHeight: '12px',
}))

const StyledDeleteButton = styled(Button)(() => ({
  color: '#ff5252',
  marginLeft: '-10px',
}))

export default API
